/* eslint-disable max-len */
import { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { setPlayerTeam, addPriority } from 'redux/slices';
import CurrentPlayerCard from './CurrentPlayerCard';
import { GenerateBalancedTeamBtn } from './GenerateBalancedTeamBtn';
import { GenerateTwoTeamsBtn } from './GenerateTwoTeamsBtn';
import { SaveTeamForNextMatchBtn } from './SaveTeamForNextMatchBtn';
import { NextTeamDialog } from './NextTeamDialog';
import { suggestNextTeam } from '../utils/suggestTeam';
import { planFillTeam } from '../utils/teamOps';
import { getPlayerPoints } from '../utils/points';

export function CurrentPlayers() {
  const dispatch = useDispatch();
  const players = useSelector((state) => state.players);
  const teams = useSelector((state) => state.teams);
  const game = useSelector((state) => state.game);
  const { gameStarted, playersPerTeam } = game;
  const teamAMembers = players.filter((p) => p.teamId === 1);
  const teamBMembers = players.filter((p) => p.teamId === 2);

  const [nextTeamOpen, setNextTeamOpen] = useState(false);
  const suggestion = useMemo(() => suggestNextTeam({
    players,
    playersPerTeam,
    genderBalanceMode: game.genderBalanceMode,
    genderRestrictedMode: game.genderRestrictedMode,
    femalesPerTeam: game.femalesPerTeam,
    setterRestrictedMode: game.setterRestrictedMode,
    autoBalanceMode: game.autoBalanceMode,
  }), [
    players,
    playersPerTeam,
    game.genderBalanceMode,
    game.genderRestrictedMode,
    game.femalesPerTeam,
    game.setterRestrictedMode,
    game.autoBalanceMode,
  ]);
  const handleFillTeam = (playerIds, priorityIds = []) => {
    const plan = planFillTeam({
      players, playersPerTeam, playerIds, priorityIds,
    });
    if (!plan) return;
    plan.playerIds.forEach((playerId) => {
      dispatch(setPlayerTeam({ playerId, teamId: plan.targetTeamId }));
    });
    plan.priorityIds.forEach((playerId) => dispatch(addPriority(playerId)));
  };
  const handleApplyAdjustment = (adjustment) => {
    adjustment.setTeamOps.forEach((op) => dispatch(setPlayerTeam(op)));
    adjustment.priorityIds.forEach((playerId) => dispatch(addPriority(playerId)));
  };

  const noPlayersInTeamA = teamAMembers.length === 0;
  const noPlayersInTeamB = teamBMembers.length === 0;
  const teamAIsPartial = teamAMembers.length > 0 && teamAMembers.length < playersPerTeam;
  const teamBIsPartial = teamBMembers.length > 0 && teamBMembers.length < playersPerTeam;

  const noPlayersInBothTeams = noPlayersInTeamA && noPlayersInTeamB;
  const teamCHasPlayers = players.some((p) => p.teamId === 3);

  const totalValueTeamA = teamAMembers.reduce((acc, curr) => acc + getPlayerPoints(curr), 0);
  const totalValueTeamB = teamBMembers.reduce((acc, curr) => acc + getPlayerPoints(curr), 0);

  const playerTypeOrder = {
    Both: 1,
    Attacker: 2,
    Setter: 3,
    Flex: 4,
  };

  const sortTeamMembers = (teamMembers) => teamMembers.sort((a, b) => playerTypeOrder[a.playerType] - playerTypeOrder[b.playerType]);

  const teamA = sortTeamMembers(teamAMembers);
  const teamB = sortTeamMembers(teamBMembers);

  return (
    <>
      {(noPlayersInBothTeams && !teamCHasPlayers)
        ? (
          <Box
            sx={{
              mt: 4,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <GenerateTwoTeamsBtn />
          </Box>
        )
        : (
          <Box
            sx={{
              mt: 4,
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginRight: 2,
              }}
            >
              <Typography variant="h5" textAlign="center">
                Time A (
                {totalValueTeamA}
                )
                {' '}
                {teams[0].winStrike ? `[Vitórias: ${teams[0].winStrike}]` : ''}
              </Typography>
              {teamA.map((player) => (
                <CurrentPlayerCard key={`${player.playerId}-${player.playerName}`} player={player} />
              ))}
              {noPlayersInTeamA && (
                <Button variant="contained" onClick={() => setNextTeamOpen(true)}>Próximos jogadores</Button>
              )}
              {teamAIsPartial && (<GenerateBalancedTeamBtn teamId={1} label={`Preencher vaga${playersPerTeam - teamAMembers.length > 1 ? 's' : ''}`} />)}
              {!gameStarted && <SaveTeamForNextMatchBtn teamId={1} canSave={!noPlayersInTeamA} />}
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginRight: 2,
              }}
            >
              <Typography variant="h5" textAlign="center">
                Time B (
                {totalValueTeamB}
                )
                {' '}
                {teams[1].winStrike ? `[Vitórias: ${teams[1].winStrike}]` : ''}
              </Typography>
              {teamB.map((player) => (
                <CurrentPlayerCard key={`${player.playerId}-${player.playerName}`} player={player} />
              ))}
              {noPlayersInTeamB && (
                <Button variant="contained" onClick={() => setNextTeamOpen(true)}>Próximos jogadores</Button>
              )}
              {teamBIsPartial && (<GenerateBalancedTeamBtn teamId={2} label={`Preencher vaga${playersPerTeam - teamBMembers.length > 1 ? 's' : ''}`} />)}
              {!gameStarted && <SaveTeamForNextMatchBtn teamId={2} canSave={!noPlayersInTeamB} />}
            </Box>
          </Box>
        )}
      <NextTeamDialog
        open={nextTeamOpen}
        onClose={() => setNextTeamOpen(false)}
        players={players}
        playersPerTeam={playersPerTeam}
        suggestion={suggestion}
        config={game}
        onFillTeam={handleFillTeam}
        onApplyAdjustment={handleApplyAdjustment}
      />
    </>
  );
}
